import React from 'react';
import { Box, Typography, Card, CardContent, Chip, Button, Grid } from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { Camera, Play, Clock, CheckCircle, Eye } from 'lucide-react';
import { ICompanyDetails } from '../../../redux/company/types';

interface ICampaignsTab {
    companyDetails?: ICompanyDetails;
}

const CampaignsTab: React.FC<ICampaignsTab> = ({ companyDetails }) => {
    const { themeStyle } = useThemeContext();

    const activeCampaigns = [
        {
            title: 'EcoTech Brand Campaign',
            client: 'Sustainable Technology Startup',
            status: 'Active',
            statusColor: '#10B981',
            icon: Camera,
            iconColor: '#3B82F6',
            description:
                'Complete brand identity and digital campaign for an innovative sustainable technology company.',
            budget: '$25,000',
            timeline: '8 weeks',
            teamSize: '3-5',
            skills: ['Brand Identity', 'Digital Marketing', 'Sustainability'],
            deadline: 'Applications close in 5 days',
        },
        {
            title: 'Fashion Week Social Campaign',
            client: 'Luxury Fashion Brand',
            status: 'Urgent',
            statusColor: '#F59E0B',
            icon: Play,
            iconColor: '#8B5CF6',
            description: 'Create engaging social media content for New York Fashion Week.',
            budget: '$40,000',
            timeline: '4 weeks',
            teamSize: '6-8',
            skills: ['Fashion', 'Social Media', 'Photography'],
            deadline: 'Applications close in 2 days',
        },
    ];

    const pastCampaigns = [
        {
            title: 'FinTech App Launch',
            client: 'Completed Q4 2023',
            status: 'Completed',
            reach: '2.1M reach',
            team: '5 creatives',
            description: 'Complete brand identity and app design for a revolutionary fintech startup.',
        },
        {
            title: 'Holiday Retail Campaign',
            client: 'Completed Q4 2023',
            status: 'Completed',
            reach: '5.2M reach',
            team: '8 creatives',
            description: 'Multi-channel holiday campaign for major retail brand.',
        },
    ];

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 4,
                }}>
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                        }}>
                        Active Campaigns
                    </Typography>
                    <Typography
                        sx={{
                            color: theme[themeStyle].gray_text,
                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                            fontSize: '0.875rem',
                        }}>
                        Current creative projects seeking talent
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    sx={{
                        borderColor: theme[themeStyle].tag_white_light,
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                    }}>
                    View All
                </Button>
            </Box>

            <Box sx={{ mb: 6 }}>
                {activeCampaigns.map((campaign, index) => (
                    <Card
                        key={index}
                        sx={{
                            backgroundColor: theme[themeStyle].pure_black,
                            border: `1px solid ${theme[themeStyle].tag_white_light}`,
                            borderLeft: `4px solid ${campaign.iconColor}`,
                            borderRadius: 2,
                            mb: 2,
                        }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    mb: 3,
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            backgroundColor: `${campaign.iconColor}20`,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        <campaign.icon size={24} color={campaign.iconColor} />
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.BOLD_HEADER,
                                            }}>
                                            {campaign.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].gray_text,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            {campaign.client}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Chip
                                    label={campaign.status}
                                    size="small"
                                    sx={{
                                        backgroundColor: `${campaign.statusColor}20`,
                                        color: campaign.statusColor,
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                    mb: 3,
                                }}>
                                {campaign.description}
                            </Typography>

                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={4}>
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            p: 2,
                                            backgroundColor: theme[themeStyle].prisma_dark,
                                            borderRadius: 2,
                                        }}>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.BOLD_HEADER,
                                                fontWeight: 'semibold',
                                            }}>
                                            {campaign.budget}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].gray_text,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            Campaign Budget
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            p: 2,
                                            backgroundColor: theme[themeStyle].prisma_dark,
                                            borderRadius: 2,
                                        }}>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.BOLD_HEADER,
                                                fontWeight: 'semibold',
                                            }}>
                                            {campaign.timeline}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].gray_text,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            Timeline
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            p: 2,
                                            backgroundColor: theme[themeStyle].prisma_dark,
                                            borderRadius: 2,
                                        }}>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.BOLD_HEADER,
                                                fontWeight: 'semibold',
                                            }}>
                                            {campaign.teamSize}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].gray_text,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            Team Size
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2,
                                }}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {campaign.skills.map((skill, skillIndex) => (
                                        <Chip
                                            key={skillIndex}
                                            label={skill}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                borderColor: theme[themeStyle].tag_white_light,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            }}
                                        />
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Clock size={16} color={theme[themeStyle].gray_text} />
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].gray_text,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontSize: '0.875rem',
                                        }}>
                                        {campaign.deadline}
                                    </Typography>
                                </Box>
                            </Box>

                            <Button
                                fullWidth
                                sx={{
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].pure_black,
                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        opacity: 0.8,
                                    },
                                }}>
                                Submit Portfolio
                            </Button>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* Past Campaigns */}
            <Typography
                variant="h6"
                sx={{
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.BOLD_HEADER,
                    mb: 3,
                }}>
                Past Campaigns
            </Typography>

            <Grid container spacing={2}>
                {pastCampaigns.map((campaign, index) => (
                    <Grid item xs={12} md={6} key={index}>
                        <Card
                            sx={{
                                backgroundColor: theme[themeStyle].pure_black,
                                border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                borderRadius: 2,
                            }}>
                            <CardContent sx={{ p: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        mb: 2,
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                backgroundColor: '#10B98120',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <CheckCircle size={20} color="#10B981" />
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontWeight: 'medium',
                                                }}>
                                                {campaign.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].gray_text,
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontSize: '0.875rem',
                                                }}>
                                                {campaign.client}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Chip
                                        label={campaign.status}
                                        size="small"
                                        sx={{
                                            backgroundColor: theme[themeStyle].prisma_dark,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        }}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        fontSize: '0.875rem',
                                        mb: 2,
                                    }}>
                                    {campaign.description}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].gray_text,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontSize: '0.875rem',
                                        }}>
                                        Team: {campaign.team}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Eye size={16} color={theme[themeStyle].gray_text} />
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].gray_text,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            {campaign.reach}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export { CampaignsTab };
